// src/components/CommandPalette.tsx
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRole, Role } from '@/lib/authz'

type Cmd = {
  id: string
  label: string
  hint?: string
  to?: string
  run?: () => void
  roles?: Role[]
}

const CMDS: Cmd[] = [
  { id:'go-loads', label:'Go to Loads', to:'/loads' },
  { id:'go-loads-inbox', label:'Open Loads Inbox', to:'/loads/inbox', roles:['Admin','Dispatcher'] },
  { id:'paper-entry', label:'New Load (Paper Entry)', to:'/loads/paper-entry', roles:['Admin','Dispatcher'] },
  { id:'import-tickets', label:'Import Scale Tickets', to:'/loads/import', roles:['Admin','Dispatcher','Accountant'] },
  { id:'import-templates', label:'Manage Import Templates', to:'/loads/import-templates', roles:['Admin','Dispatcher','Accountant'] },
  { id:'weighbridge', label:'Weighbridge Settings', to:'/admin/weighbridge', roles:['Admin'] },
  { id:'audit', label:'Audit Log', to:'/admin/audit-log', roles:['Admin','Accountant'] },
  { id:'sites', label:'Sites', to:'/sites' },
  { id:'products', label:'Products', to:'/products' },
  { id:'jobs', label:'Jobs', to:'/jobs' },
  { id:'rate-history', label:'Rate History', to:'/jobs/rate-history' },
  { id:'settlement', label:'Settlement Builder', to:'/settlements/builder', roles:['Admin','Accountant'] },
  { id:'pnl', label:'Profit & Loss', to:'/reports/pnl', roles:['Admin','Accountant'] },
  { id:'reports', label:'Reports', to:'/reports' },
  { id:'settings', label:'Settings', to:'/admin/settings', roles:['Admin'] },
]

export default function CommandPalette({
  open,
  onClose
}:{ open: boolean; onClose: () => void }){
  const [q, setQ] = useState('')
  const nav = useNavigate()
  const role = useRole()

  // Close on Escape (Header toggles open on Ctrl+K)
  useEffect(()=>{
    function onKey(e: KeyboardEvent){
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const cmds = useMemo(()=>{
    const allowed = CMDS.filter(c => !c.roles || c.roles.includes(role))
    const s = q.trim().toLowerCase()
    return s ? allowed.filter(c => (c.label+' '+(c.hint||'')).toLowerCase().includes(s)) : allowed
  }, [q, role])

  function exec(c: Cmd){
    if (c.run) c.run()
    if (c.to) nav(c.to)
    onClose()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/30 grid place-items-start pt-24" onClick={onClose}>
      <div
        className="w-[680px] max-w-[92vw] bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden"
        onClick={e=>e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200">
          <input
            autoFocus
            className="input"
            placeholder="Type a command… (Esc to close)"
            value={q}
            onChange={e=>setQ(e.target.value)}
          />
        </div>
        <div className="max-h-[50vh] overflow-auto">
          {cmds.map(c=>(
            <button
              key={c.id}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100"
              onClick={()=>exec(c)}
            >
              <div className="text-sm">{c.label}</div>
              {c.hint && <div className="text-xs text-slate-500">{c.hint}</div>}
            </button>
          ))}
          {cmds.length === 0 && (
            <div className="px-4 py-6 text-sm text-slate-500">No matches.</div>
          )}
        </div>
      </div>
    </div>
  )
}
